import { CSSProperties, FC } from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { TPersonData, cn, FaIcon } from '@fe-learner-common';
import { faUser } from '@fortawesome/pro-regular-svg-icons/faUser';

export type AvatarUserProps = {
  src?: string;
  alt: string;
  firstName?: string;
  lastName?: string;
  online?: TPersonData['status'];
};

export type AvatarComponentProps = {
  size: 'xs' | 'md' | 'lg' | 'xl' | '2xl';
};

type AvatarProps = AvatarUserProps &
  AvatarComponentProps & {
    className?: string;
    style?: CSSProperties;
  };

const sizeMap = {
  xs: 'w-5 h-5 text-label-sm text-[8px]',
  md: 'w-6 h-6 text-label-sm text-[9px]',
  lg: 'w-8 h-8 text-label-sm',
  xl: 'w-10 h-10 text-label-md',
  '2xl': 'w-[72px] h-[72px] text-label-lg',
};

const getInitials = (firstName?: string, lastName?: string) => {
  if (!firstName || !lastName) return null;

  return `${firstName[0]}${lastName[0]}`;
};

export const Avatar: FC<AvatarProps> = ({
  className,
  style,
  alt,
  src,
  size,
  firstName,
  lastName,
  online,
  ...rest
}) => {
  if (!src) {
    return (
      <AvatarPlaceholder
        ariaLabel={alt}
        size={size}
        className={className}
        style={style}
      />
    );
  }

  return (
    <AvatarPrimitive.Root
      style={style}
      className={cn(
        'relative inline-flex select-none items-center justify-center rounded-full align-middle',
        sizeMap[size],
        className,
      )}
      {...rest}
    >
      <AvatarPrimitive.Image
        className="h-full w-full rounded-[inherit] object-cover"
        src={src}
        alt={alt}
      />
      <AvatarPrimitive.Fallback
        className={cn(
          'leading-1 flex h-full w-full animate-pulse items-center justify-center rounded-full bg-neutral-300 text-neutral-0',
        )}
      >
        {getInitials(firstName, lastName)}
      </AvatarPrimitive.Fallback>
      {online === 'online' && (
        <span
          className={cn(
            'absolute bottom-0 right-0 z-10 h-2 w-2 rounded-xl bg-positive-500 outline outline-2 outline-neutral-0 group-hover:outline-neutral-100',
          )}
        ></span>
      )}
    </AvatarPrimitive.Root>
  );
};

export type AvatarPlaceholderProps = {
  ariaLabel: string;
  className?: string;
  style?: CSSProperties;
} & AvatarComponentProps;

const iconSizeMap = {
  xs: 10,
  md: 12,
  lg: 16,
  xl: 20,
  '2xl': 36,
};

export const AvatarPlaceholder: FC<AvatarPlaceholderProps> = ({
  ariaLabel,
  size,
  className,
  style,
}) => {
  const iconSize = iconSizeMap[size];

  return (
    <AvatarPrimitive.Root
      aria-label={ariaLabel}
      style={style}
      className={cn(
        'inline-flex items-center justify-center rounded-full',
        sizeMap[size],
        className,
      )}
    >
      <AvatarPrimitive.Fallback className="flex h-full w-full items-center justify-center rounded-full bg-neutral-200">
        <FaIcon
          icon={faUser}
          className="text-neutral-550"
          width={iconSize}
          height={iconSize}
        />
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
};
